import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useMutation } from '@tanstack/react-query'
import { useApiClientUser } from '~/@api/client/useApiClientUser'
import type { IUserSetBrokerCodeRequest } from '~/@api/generated-api-types-user/Api'

type BrokerCodeState = {
  brokerCode: string | null
  brokerCodeRememberedAt: string | null
  setBrokerCode: (brokerCode: string, brokerCodeRememberedAt: string) => void
  getBrokerCode: () => string | null
  getBrokerCodeRememberedAt: () => string | null
  clearBrokerCode: () => void
}

function useBrokerCode() {
  const client = useApiClientUser()
  const brokerCodeStore = useBrokerCodeStore()

  const setBrokerCodeMutation = useMutation({
    mutationFn: async (params: IUserSetBrokerCodeRequest) => {
      return await client.user.onboardingControllerSetBrokerCode(params, {
        secure: true,
      })
    },
  })
  return {
    setBrokerCodeMutation,
    brokerCode: brokerCodeStore.getBrokerCode(),
    brokerCodeRememberedAt: brokerCodeStore.getBrokerCodeRememberedAt(),
    setBrokerCode: brokerCodeStore.setBrokerCode,
    clearBrokerCode: brokerCodeStore.clearBrokerCode,
  }
}

function useBrokerCodeStore() {
  const storageName = 'broker-code-store'
  const storage = {
    getItem: (name: string) => {
      const str = localStorage.getItem(name)
      return str ? JSON.parse(str) : null
    },
    setItem: (name: string, value: any) => {
      localStorage.setItem(name, JSON.stringify(value))
    },
    removeItem: (name: string) => {
      localStorage.removeItem(name)
    },
  }

  const createStore = create<BrokerCodeState>()(
    persist(
      (set, get) => ({
        brokerCode: null,
        brokerCodeRememberedAt: null,
        setBrokerCode: (brokerCode: string, brokerCodeRememberedAt: string) => {
          set({ brokerCode, brokerCodeRememberedAt })
        },
        getBrokerCode: () => get().brokerCode,
        getBrokerCodeRememberedAt: () => get().brokerCodeRememberedAt,
        clearBrokerCode: () => {
          set({ brokerCode: null, brokerCodeRememberedAt: null })
          storage.removeItem(storageName)
        },
      }),
      {
        name: storageName,
        storage,
      }
    )
  )
  return createStore()
}

export { useBrokerCode }
